import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import Image from 'next/image'

import {
    ArrowLeft,
    CalendarCheck,
    FileText,
    LockKeyhole,
    Megaphone
} from 'lucide-react'

import { Logo } from '@/components/generals/logo'

interface AuthShellProps {
    eyebrow: string
    title: ReactNode
    intro: string
    children: ReactNode
}

const portalFeatures = [
    { label: "Estate notices from the office and trustees", icon: Megaphone },
    { label: "Rules and guidelines to download as PDFs", icon: FileText },
    { label: "Visitor bookings for the gate", icon: CalendarCheck }
]

/*
   Resident access layout
   Split screen on desktop: a quiet photograph panel with the Rockwood mark on
   the left, the form on warm cream on the right. Stacks to the form alone on
   smaller screens.
*/
export const AuthShell: FC<AuthShellProps> = ({ eyebrow, title, intro, children }): ReactNode => {
    return (
        <div className='flex min-h-screen bg-cream'>
            <aside className='relative hidden w-[44%] max-w-[720px] overflow-hidden bg-forest-deep text-cream lg:block'>
                <Image
                    src='/images/fireplace-lounge.jpg'
                    alt=''
                    fill
                    priority
                    sizes='44vw'
                    className='object-cover opacity-60'
                />

                <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/50 to-forest-deep/20' />

                <div className='relative flex h-full flex-col justify-between p-12 xl:p-16'>
                    <Link href='/' aria-label='Rockwood Estate, home' className='w-fit'>
                        <Logo tone='light' />
                    </Link>

                    <div>
                        <p className='font-heading text-5xl leading-[1.05]'>
                            Welcome <em className='italic text-cream/80'>home.</em>
                        </p>

                        <p className='mt-5 max-w-sm text-sm leading-relaxed text-cream/75'>
                            The private portal for the residents of Rockwood Estate's fourteen homes.
                        </p>

                        <ul className='mt-10 space-y-4 border-t border-cream/15 pt-8'>
                            {portalFeatures.map(feature => {
                                const Icon = feature.icon

                                return (
                                    <li key={feature.label} className='flex items-center gap-4 text-sm text-cream/85'>
                                        <span className='inline-flex size-9 items-center justify-center rounded-full border border-cream/20'>
                                            <Icon aria-hidden='true' className='size-4' />
                                        </span>

                                        {feature.label}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                </div>
            </aside>

            <div className='flex min-h-screen flex-1 flex-col'>
                <div className='flex items-center justify-between gap-4 px-6 py-6 sm:px-10'>
                    <Link href='/' aria-label='Rockwood Estate, home' className='lg:invisible'>
                        <Logo />
                    </Link>

                    <Link href='/' className='inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-charcoal'>
                        <ArrowLeft aria-hidden='true' className='size-4' />
                        <span className='hidden sm:inline'>Back to the website</span>
                        <span className='sm:hidden'>Website</span>
                    </Link>
                </div>

                <main id='main-content' className='flex flex-1 items-center justify-center px-6 pb-16 pt-6 sm:px-10'>
                    <div className='w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-500'>
                        <span className='inline-flex items-center gap-2 rounded-full border border-forest/20 bg-forest-soft px-3 py-1.5 text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-forest'>
                            <LockKeyhole aria-hidden='true' className='size-3.5' />
                            {eyebrow}
                        </span>

                        <h1 className='mt-6 font-heading text-4xl leading-tight'>
                            {title}
                        </h1>

                        <p className='mt-4 leading-relaxed text-muted-foreground'>
                            {intro}
                        </p>

                        <div className='mt-10'>
                            {children}
                        </div>
                    </div>
                </main>

                <p className='px-6 pb-8 text-center text-xs leading-relaxed text-muted-foreground sm:px-10'>
                    This area is for residents of Rockwood Estate. Access is managed by the estate office.
                </p>
            </div>
        </div>
    )
}
